import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, AlertTriangle, Eye, Edit2, ChevronRight, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { nonConformityService, productionLotService, productService, clientService, supplierService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const NC_TYPES = ['internal', 'supplier', 'customer'];
const SEVERITIES = ['minor', 'major', 'critical'];
const STATUSES = ['open', 'under_analysis', 'waiting', 'closed'];
const MACRO_AREAS = ['Montaggio', 'Sbavatura', 'Controllo', 'Gestione'];

export default function NonConformitiesPage() {
  const [ncs, setNcs] = useState([]);
  const [lots, setLots] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    nc_type: 'internal',
    lot_id: '',
    product_id: '',
    client_id: '',
    supplier_id: '',
    origin: '',
    defect_description: '',
    macro_area: '',
    severity: 'minor',
    involved_quantity: '',
    containment_action: '',
    corrective_action: '',
    responsible_person: '',
    due_date: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
    loadNcs();
  }, [search, filterType, filterStatus, filterSeverity]);

  const loadData = async () => {
    try {
      const [lotsRes, productsRes, clientsRes, suppliersRes] = await Promise.all([
        productionLotService.getAll(),
        productService.getAll({ status: 'active' }),
        clientService.getAll({ is_active: true }),
        supplierService.getAll({ is_active: true })
      ]);
      setLots(lotsRes.data);
      setProducts(productsRes.data);
      setClients(clientsRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      console.error('Failed to load data');
    }
  };

  const loadNcs = async () => {
    try {
      const params = { search };
      if (filterType) params.nc_type = filterType;
      if (filterStatus) params.status = filterStatus;
      if (filterSeverity) params.severity = filterSeverity;
      const response = await nonConformityService.getAll(params);
      setNcs(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento non conformità');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNC = async (nc) => {
    if (!window.confirm(`Eliminare la non conformita ${nc.nc_number}? Verranno eliminati anche costi e allegati associati.`)) return;
    try {
      await nonConformityService.delete(nc.id);
      toast.success('Non conformita eliminata');
      loadNcs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella cancellazione');
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        lot_id: formData.lot_id || null,
        product_id: formData.product_id || null,
        client_id: formData.client_id || null,
        supplier_id: formData.supplier_id || null,
        involved_quantity: formData.involved_quantity ? parseInt(formData.involved_quantity) : null,
        due_date: formData.due_date || null
      };
      await nonConformityService.create(data);
      toast.success('Non conformità creata');
      setDialogOpen(false);
      resetForm();
      loadNcs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operazione fallita');
    }
  };

  const resetForm = () => {
    setFormData({
      nc_type: 'internal',
      lot_id: '',
      product_id: '',
      client_id: '',
      supplier_id: '',
      origin: '',
      defect_description: '',
      macro_area: '',
      severity: 'minor',
      involved_quantity: '',
      containment_action: '',
      corrective_action: '',
      responsible_person: '',
      due_date: '',
      notes: ''
    });
  };

  const getTypeBadge = (type) => {
    const styles = {
      internal: 'bg-indigo-500 text-white',
      supplier: 'bg-orange-500 text-white',
      customer: 'bg-teal-500 text-white',
    };
    return styles[type] || styles.internal;
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      minor: 'bg-sky-500 text-white',
      major: 'bg-orange-500 text-white',
      critical: 'bg-red-600 text-white',
    };
    return styles[severity] || styles.minor;
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-red-500 text-white',
      under_analysis: 'bg-amber-500 text-white',
      waiting: 'bg-sky-500 text-white',
      closed: 'bg-emerald-500 text-white',
    };
    return styles[status] || styles.open;
  };

  return (
    <div className="space-y-6" data-testid="non-conformities-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Non Conformità</h1>
          <p className="text-sm text-slate-500 mt-1">Monitora e gestisci le problematiche qualità</p>
        </div>
        {hasRole('admin', 'quality', 'production') && (
          <Button onClick={() => setDialogOpen(true)} className="h-12 px-6 text-base" data-testid="add-nc-btn">
            <Plus className="w-5 h-5 mr-2" />
            Nuova NC
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by NC number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-ncs-input"
              />
            </div>
            <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-36">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {NC_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSeverity || "all"} onValueChange={(v) => setFilterSeverity(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-36">
                <SelectValue placeholder="All Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                {SEVERITIES.map((sev) => (
                  <SelectItem key={sev} value={sev}>{sev}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-36">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>{status.replace('_', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : ncs.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No non conformities found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Numero NC</th>
                    <th>Tipo</th>
                    <th>Data Apertura</th>
                    <th>Prodotto</th>
                    <th>Lotto</th>
                    <th>Descrizione</th>
                    <th>Gravità</th>
                    <th>Stato</th>
                    <th>Costo</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {ncs.map((nc) => (
                    <tr key={nc.id} className="row-link" onClick={() => navigate(`/non-conformities/${nc.id}`)} data-testid={`nc-row-${nc.id}`}>
                      <td className="font-mono font-medium">{nc.nc_number}</td>
                      <td>
                        <Badge className={getTypeBadge(nc.nc_type)}>{nc.nc_type}</Badge>
                      </td>
                      <td>{nc.opening_date}</td>
                      <td>{nc.product_code || '-'}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {nc.lot_code ? (
                          <Link to={`/production-lots/${nc.lot_id}`} className="font-mono text-blue-600 hover:underline font-medium">
                            {nc.lot_code}
                          </Link>
                        ) : '-'}
                      </td>
                      <td className="max-w-[200px] truncate">{nc.defect_description}</td>
                      <td>
                        <Badge className={getSeverityBadge(nc.severity)}>{nc.severity}</Badge>
                      </td>
                      <td>
                        <Badge className={getStatusBadge(nc.status)}>{nc.status?.replace('_', ' ')}</Badge>
                      </td>
                      <td className="font-semibold">
                        {nc.total_cost > 0 ? `€${nc.total_cost.toFixed(2)}` : '-'}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {hasRole('admin') && (
                            <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => handleDeleteNC(nc)} data-testid={`delete-nc-${nc.id}`}>
                              <Trash2 className="w-4 h-4 text-rose-500" />
                            </Button>
                          )}
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Non Conformity</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>NC Type *</Label>
                <Select
                  value={formData.nc_type}
                  onValueChange={(value) => setFormData({ ...formData, nc_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NC_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Severity *</Label>
                <Select
                  value={formData.severity}
                  onValueChange={(value) => setFormData({ ...formData, severity: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map((sev) => (
                      <SelectItem key={sev} value={sev}>{sev}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Lotto di Produzione</Label>
                <Select
                  value={formData.lot_id || "none"}
                  onValueChange={(value) => {
                    const lotId = value === "none" ? "" : value;
                    const selectedLot = lots.find(l => l.id === lotId);
                    setFormData({
                      ...formData,
                      lot_id: lotId,
                      product_id: selectedLot?.product_id || formData.product_id,
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona lotto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun lotto</SelectItem>
                    {lots.map((lot) => (
                      <SelectItem key={lot.id} value={lot.id}>{lot.lot_code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prodotto</Label>
                <Select
                  value={formData.product_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, product_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No product</SelectItem>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id}>{product.code} - {product.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.nc_type === 'customer' && (
                <div className="space-y-2">
                  <Label>Client</Label>
                  <Select
                    value={formData.client_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, client_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No client</SelectItem>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {formData.nc_type === 'supplier' && (
                <div className="space-y-2">
                  <Label>Supplier</Label>
                  <Select
                    value={formData.supplier_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, supplier_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No supplier</SelectItem>
                      {suppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>{supplier.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Macro Area</Label>
                <Select
                  value={formData.macro_area || "none"}
                  onValueChange={(value) => setFormData({ ...formData, macro_area: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select area" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No area</SelectItem>
                    {MACRO_AREAS.map((area) => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Involved Quantity</Label>
                <Input
                  type="number"
                  value={formData.involved_quantity}
                  onChange={(e) => setFormData({ ...formData, involved_quantity: e.target.value })}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Defect Description *</Label>
                <Textarea
                  value={formData.defect_description}
                  onChange={(e) => setFormData({ ...formData, defect_description: e.target.value })}
                  required
                  rows={3}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Containment Action</Label>
                <Textarea
                  value={formData.containment_action}
                  onChange={(e) => setFormData({ ...formData, containment_action: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Responsible Person</Label>
                <Input
                  value={formData.responsible_person}
                  onChange={(e) => setFormData({ ...formData, responsible_person: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-nc-btn">Create NC</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
